import _ from "lodash";
import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";
import withLoader from "UIComponents/GqlWithLoader";
import { withRouter } from "react-router";
import { compose, graphql } from "lib/apolloClient";
import { getStaffListEnhancer } from "../../../modules/EnhanceQueries/LELibraryQueryEnhancer";
import {
  FILTER_NAME_ACADEMIC_YEAR,
  FILTER_NAME_GRADES,
  FILTER_NAME_CREATORS,
  FILTER_NAME_POST_TYPE,
  FILTER_NAME_SUBJECTS,
  FILTER_NAME_COURSES,
} from "Course/Constants/StringConstants";
import { useI18n } from "Hooks";
import {
  AI_TUTOR,
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_UBD,
} from "Constants/stringConstants";
import { getAcademicYearsListAndGradeList } from "Course/modules/CourseModule";
import ACLStore from "lib/aclStore";
import { getDynamicAssessmentTypeOptions } from "Course/routes/ClassRoom/modules/utils";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import {
  ENABLE_AI_TUTOR,
  ENABLE_ASSESSMENTS_IN_PYP,
} from "Constants/permissionConstants";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import {
  getSelectedAcademicYear,
  getSelectedAcademicYearId,
  getUserCurriculumPrograms,
} from "modules/CommonSelectors";
import { getAcademicYearsList } from "modules/Services";
import { getIsAssessmentsOptionsDisabled } from "Utils/EntityFlowUtils";
import {
  getIsCourseBasedGradingEnabled,
  getIsLearningCoursesEnabled,
} from "LearningCourses/modules/Utils";
import {
  getCoursesFeedQuery,
  getOrganizationSubjectsQuery,
} from "SchoolLibrary/modules/Queries";
import { getCourseList } from "SchoolLibrary/routes/UnitLibrary/modules/Utils";
import { getOrganizationSubjectsFromCache } from "SchoolLibrary/modules/GraphqlHelpers";
import { getOrganizationDetailsFromCache } from "modules/CommonGraphqlHelpers";
import { getFilteredGrades } from "Teacher/routes/SchoolLibrary/modules/Utils";

const getDefaultSubject = ({
  //@Arham: This function is not required
  courseId,
  curriculumProgramType,
  courseGradeFilters,
}) => {
  if (
    !_.includes(
      [
        CURRICULUM_TYPE_PYP,
        CURRICULUM_TYPE_MYP,
        CURRICULUM_TYPE_UBD,
        CURRICULUM_TYPE_DP,
      ],
      curriculumProgramType
    )
  ) {
    return null;
  }

  const course = getCourseDetailsFromCache({
    id: courseId,
    courseGradeFilters,
  });

  const subject = _.head(_.get(course, "subjects", []));
  return _.get(subject, "id", null);
};

const getPostFilterOptions = ({
  curriculumProgramType,
  t,
  shouldIncludeQuickTasks,
  groupType,
  isWorksheetEnabled,
  hideAssessments,
}) => {
  if (groupType === "WORKSHEET") {
    // * Returning only worksheet option for worksheet grouptype for any programme
    return isWorksheetEnabled
      ? [
          {
            label: t("common:worksheet"),
            value: "WORKSHEET",
          },
        ]
      : [];
  }

  const aiChatTaskFilter = ACLStore.can(ENABLE_AI_TUTOR)
    ? [
        {
          label: t("common:ai_tutor"),
          value: AI_TUTOR,
        },
      ]
    : [];

  switch (curriculumProgramType) {
    case CURRICULUM_TYPE_DP:
    case CURRICULUM_TYPE_MYP: {
      const quickTaskFilter = shouldIncludeQuickTasks
        ? [
            {
              label: t("common:quick_task"),
              value: "QUICK_TASK",
            },
          ]
        : [];
      const worksheetFilter = isWorksheetEnabled
        ? [
            {
              label: t("common:worksheet"),
              value: "WORKSHEET",
            },
          ]
        : [];
      let postTypeFilter;
      // for importing LE in unit plan
      if (groupType) {
        postTypeFilter =
          groupType === "LEARNING_ENGAGEMENT"
            ? [
                {
                  label: t("common:le_label"),
                  value: "LEARNING_ENGAGEMENT_MYP",
                },
              ]
            : [
                {
                  label: t("common:formative_assessment"),
                  value: "FORMATIVE_ASSESSMENT_MYP",
                },
                {
                  label: t("common:summative_assessment"),
                  value: "SUMMATIVE_ASSESSMENT_MYP",
                },
              ];
      } else {
        postTypeFilter = [
          { label: t("common:le_label"), value: "LEARNING_ENGAGEMENT_MYP" },
          {
            label: t("common:formative_assessment"),
            value: "FORMATIVE_ASSESSMENT_MYP",
          },
          {
            label: t("common:summative_assessment"),
            value: "SUMMATIVE_ASSESSMENT_MYP",
          },
        ];
      }
      return [
        ...quickTaskFilter,
        ...(hideAssessments
          ? []
          : [...postTypeFilter, ...worksheetFilter, ...aiChatTaskFilter]),
      ];
    }

    case CURRICULUM_TYPE_UBD: {
      const aiChatTaskFilterForUBD = ACLStore.can(ENABLE_AI_TUTOR)
        ? [
            {
              label: t("common:ai_tutor"),
              value: "AI_TUTOR_UBD",
            },
          ]
        : [];
      const quickTaskFilter = shouldIncludeQuickTasks
        ? [
            {
              label: t("common:quick_task"),
              value: "QUICK_TASK_UBD",
            },
          ]
        : [];
      const worksheetFilter = isWorksheetEnabled
        ? [
            {
              label: t("common:worksheet"),
              value: "WORKSHEET_UBD",
            },
          ]
        : [];
      let postTypeFilter;
      if (groupType) {
        postTypeFilter =
          groupType === "LEARNING_ENGAGEMENT"
            ? [
                {
                  label: t("common:le_label"),
                  value: "LEARNING_ENGAGEMENT_UBD",
                },
              ]
            : [
                {
                  label: t("common:assessment_label_plural"),
                  value: "ASSESSMENT_UBD",
                },
              ];
      } else {
        postTypeFilter = [
          { label: t("common:le_label"), value: "LEARNING_ENGAGEMENT_UBD" },
          {
            label: t("common:assessment_label_plural"),
            value: "ASSESSMENT_UBD",
          },
        ];
      }
      return [
        ...quickTaskFilter,
        ...postTypeFilter,
        ...worksheetFilter,
        ...aiChatTaskFilterForUBD,
      ];
    }

    case CURRICULUM_TYPE_PYP: {
      const quickTaskFilter = shouldIncludeQuickTasks
        ? [
            {
              label: t("common:quick_task"),
              value: "QUICK_TASK",
            },
          ]
        : [];
      const worksheetFilter = isWorksheetEnabled
        ? [
            {
              label: t("common:worksheet"),
              value: "WORKSHEET",
            },
          ]
        : [];

      const assessmentFilter = ACLStore.can(ENABLE_ASSESSMENTS_IN_PYP)
        ? [
            {
              label: t("common:formative_assessment"),
              value: "FORMATIVE_ASSESSMENT_PYP",
            },
            {
              label: t("common:summative_assessment"),
              value: "SUMMATIVE_ASSESSMENT_PYP",
            },
          ]
        : [];

      let postTypeFilter = [];

      if (groupType) {
        postTypeFilter =
          groupType === "LEARNING_ENGAGEMENT"
            ? [{ label: t("common:le_label"), value: "LEARNING_ENGAGEMENT" }]
            : assessmentFilter;
      } else {
        postTypeFilter = [
          { label: t("common:le_label"), value: "LEARNING_ENGAGEMENT" },
          ...assessmentFilter,
        ];
      }
      return [
        ...quickTaskFilter,
        ...postTypeFilter,
        ...worksheetFilter,
        ...aiChatTaskFilter,
      ];
    }

    default: {
      return [];
    }
  }
};
const LELibraryFiltersHOC = (WrapperComponent, { skipLoader = false } = {}) => {
  const WithLELibraryFilters = props => {
    const {
      academicYearsList,
      gradeList,
      creatorList,
      subjectsList,
      courseList,
      isLearningCoursesEnabled,
      organizationId,
      curriculumProgramType,
      shouldIncludeQuickTasks = true,
      hideCoursesFilter = false,
      groupType,
      courseId,
      courseGradeFilters,
      hideAssessments,
      currentUserId,
      curriculumProgramId,
      schoolsData,
      isCombinedFiltersEnabled,
      hasNextPageForCreatorFilter,
      isFetchingMoreDataForCreatorFilter,
      loadMoreCreatorFilterFeed,
    } = props;

    const { t } = useI18n(["common"]);
    const isWorksheetEnabled =
      ACLStore.can("FeatureFlag:LeFeedWorksheet") &&
      !_.includes(["ASSESSMENT", "LEARNING_ENGAGEMENT"], groupType);

    const isAssessmentTypeConfigurationEnabled = ACLStore.can(
      "FeatureFlag:AssessmentTypeConfiguration"
    );

    const isUserInCreatorsList = _.some(creatorList, { value: currentUserId });
    const createdByFilter = isUserInCreatorsList ? [currentUserId] : [];

    const handleCreatorFilterScrollEnd = useCallback(() => {
      if (!isFetchingMoreDataForCreatorFilter && hasNextPageForCreatorFilter) {
        loadMoreCreatorFilterFeed();
      }
    }, [
      hasNextPageForCreatorFilter,
      isFetchingMoreDataForCreatorFilter,
      loadMoreCreatorFilterFeed,
    ]);

    const getPostTypeOptions = useCallback(
      ({ curriculumProgramId, curriculumProgramType }) => {
        let postTypeList;

        // Check if dynamic assessment types should be used
        if (
          isAssessmentTypeConfigurationEnabled &&
          !_.isEmpty(curriculumProgramId)
        ) {
          const dynamicTypeOptions = getDynamicAssessmentTypeOptions({
            curriculumId: curriculumProgramId,
          });

          postTypeList = _.map(dynamicTypeOptions, option => ({
            label: _.get(option, "label"),
            value: _.get(option, "value"),
          }));
        } else {
          // Fallback to static options
          postTypeList = getPostFilterOptions({
            curriculumProgramType,
            t,
            shouldIncludeQuickTasks,
            groupType,
            isWorksheetEnabled,
            hideAssessments,
          });
        }

        return postTypeList;
      },
      [
        groupType,
        hideAssessments,
        isAssessmentTypeConfigurationEnabled,
        isWorksheetEnabled,
        shouldIncludeQuickTasks,
        t,
      ]
    );

    const filtersInfo = useMemo(() => {
      return [
        {
          id: FILTER_NAME_COURSES,
          name: FILTER_NAME_COURSES,
          options: courseList,
          label: "course:course",
          showSearchBar: true,
          showAllOption: false,
          noOptionLabel: t("common:select"),
          toolTipText: t("common:select_label_first", {
            label: t("common:grade"),
          }),
          visibleToCurriculum: [
            CURRICULUM_TYPE_PYP,
            CURRICULUM_TYPE_MYP,
            CURRICULUM_TYPE_UBD,
          ],
          type: "CHECKLIST_DROPDOWN",
          showSelectedOptionsOnTop: true,
          isLocalizedNoOptionLabel: true,
          isCheckList: true,
          isHidden: !isLearningCoursesEnabled || hideCoursesFilter,
        },
        {
          id: FILTER_NAME_ACADEMIC_YEAR,
          name: FILTER_NAME_ACADEMIC_YEAR,
          options: academicYearsList,
          label: "common:academic_year",
          showSearchBar: false,
          isCheckList: false,
          showAllOption: false,
          visibleToCurriculum: [
            CURRICULUM_TYPE_PYP,
            CURRICULUM_TYPE_MYP,
            CURRICULUM_TYPE_UBD,
            CURRICULUM_TYPE_DP,
          ],
          noOptionLabel: t("common:select"),
          showSelectedOptionsOnTop: false,
          type: "CHECKLIST_DROPDOWN",
          isLocalizedNoOptionLabel: true,
        },
        {
          id: FILTER_NAME_GRADES,
          name: FILTER_NAME_GRADES,
          options: gradeList,
          label: "common:grade",
          showSearchBar: true,
          noOptionLabel: t("common:select"),
          isCheckList: true,
          showAllOption: true,
          visibleToCurriculum: [
            CURRICULUM_TYPE_PYP,
            CURRICULUM_TYPE_MYP,
            CURRICULUM_TYPE_UBD,
            CURRICULUM_TYPE_DP,
          ],
          type: "CHECKLIST_DROPDOWN",
          showSelectedOptionsOnTop: false,
          isLocalizedNoOptionLabel: true,
        },
        {
          id: FILTER_NAME_SUBJECTS,
          name: FILTER_NAME_SUBJECTS,
          options: subjectsList,
          label: "common:subject",
          showSearchBar: true,
          isCheckList: true,
          showAllOption: true,
          noOptionLabel: t("common:select"),
          toolTipText: t("common:select_label_first", {
            label: t("common:grade"),
          }),
          visibleToCurriculum: [
            CURRICULUM_TYPE_PYP,
            CURRICULUM_TYPE_MYP,
            CURRICULUM_TYPE_UBD,
            CURRICULUM_TYPE_DP,
          ],
          type: "CHECKLIST_DROPDOWN",
          showSelectedOptionsOnTop: false,
          isLocalizedNoOptionLabel: true,
          isHidden: getIsCourseBasedGradingEnabled(),
        },
        {
          id: FILTER_NAME_POST_TYPE,
          name: FILTER_NAME_POST_TYPE,
          options: getPostTypeOptions({
            curriculumProgramId,
            curriculumProgramType,
          }),
          label: "common:assignment_type",
          showSearchBar: false,
          isCheckList: true,
          showAllOption: true,
          noOptionLabel: t("common:select"),
          visibleToCurriculum: [
            ...(shouldIncludeQuickTasks ||
            ACLStore.can(ENABLE_ASSESSMENTS_IN_PYP)
              ? [CURRICULUM_TYPE_PYP]
              : []),
            CURRICULUM_TYPE_MYP,
            CURRICULUM_TYPE_UBD,
            CURRICULUM_TYPE_DP,
          ],
          type: "CHECKLIST_DROPDOWN",
          showSelectedOptionsOnTop: false,
          isLocalizedNoOptionLabel: true,
        },
        {
          id: FILTER_NAME_CREATORS,
          name: FILTER_NAME_CREATORS,
          options: creatorList,
          label: "common:creator",
          isCheckList: true,
          showAllOption: true,
          noOptionLabel: t("common:select"),
          isSearchBasedOnQuery: true,
          organizationId,
          graphqlQueryEnhancer: getStaffListEnhancer,
          visibleToCurriculum: [
            CURRICULUM_TYPE_PYP,
            CURRICULUM_TYPE_MYP,
            CURRICULUM_TYPE_UBD,
            CURRICULUM_TYPE_DP,
          ],
          type: "CHECKLIST_DROPDOWN",
          showSearchBar: true,
          showSelectedOptionsOnTop: true,
          isLocalizedNoOptionLabel: true,
          showArchivedOptionsAtLast: true,
          onScrollEnd: handleCreatorFilterScrollEnd,
          isLoadingMoreOptions: isFetchingMoreDataForCreatorFilter,
        },
      ];
    }, [
      courseList,
      t,
      isLearningCoursesEnabled,
      hideCoursesFilter,
      academicYearsList,
      gradeList,
      subjectsList,
      getPostTypeOptions,
      curriculumProgramId,
      curriculumProgramType,
      shouldIncludeQuickTasks,
      creatorList,
      organizationId,
      handleCreatorFilterScrollEnd,
      isFetchingMoreDataForCreatorFilter,
    ]);

    const postTypesData = useMemo(() => {
      if (isCombinedFiltersEnabled && !_.isEmpty(schoolsData)) {
        return _.chain(schoolsData)
          .flatMap(school => {
            const { id, type } = school;

            return _.map(
              getPostTypeOptions({
                curriculumProgramId: id,
                curriculumProgramType: type,
              }),
              option => ({
                ...option,
                id: option?.value,
                curriculumProgram: school,
              })
            );
          })
          .value();
      }

      return _.map(
        getPostTypeOptions({
          curriculumProgramId,
          curriculumProgramType,
        }),
        option => ({
          ...option,
          id: option?.value,
        })
      );
    }, [
      curriculumProgramId,
      curriculumProgramType,
      getPostTypeOptions,
      isCombinedFiltersEnabled,
      schoolsData,
    ]);

    const postTypeFilter = _.find(filtersInfo, { id: FILTER_NAME_POST_TYPE });
    const defaultPostType = _.map(
      _.get(postTypeFilter, "options", []),
      "value"
    );

    const newProps = {
      ...props,
      defaultCreators: createdByFilter,
      defaultPostType,
      filtersInfo: _.filter(
        filtersInfo,
        item =>
          _.includes(item.visibleToCurriculum, curriculumProgramType) &&
          !_.get(item, "isHidden", false)
      ),
      defaultSubject: getDefaultSubject({
        courseId,
        curriculumProgramType,
        courseGradeFilters,
      }),
      postTypesData,
    };
    return <WrapperComponent {...newProps} />;
  };

  const mapStateToProps = (state, ownProps) => {
    const { isCombinedFiltersEnabled, isSchoolOptionsEnabled } = ownProps;
    const filters = _.get(state, "leLibrary.filters", {});
    const userInfo = getUserInfo();
    const organizationId = userInfo.org_id;
    const curriculumProgramType = _.get(
      state,
      "platform.currentCurriculumProgram.type",
      ""
    );

    const hideAssessments = getIsAssessmentsOptionsDisabled({ state });

    const curriculumProgramId = _.get(
      state,
      "platform.currentCurriculumProgram.id",
      null
    );
    const selectedAcademicYear = getSelectedAcademicYear(state);
    const academicYearId = getSelectedAcademicYearId(state);
    const userArchivedFilters = {
      academicYearId,
    };
    const courseGradeFilters = {
      academicYearId,
    };

    const { skipStaffListQuery } = ownProps;

    const {
      academicYearsList,
      defaultAcademicYear,
      gradeList,
    } = getAcademicYearsListAndGradeList({
      organizationId,
      curriculumProgramId,
      selectedAcademicYear,
    });

    const curriculumProgramIds_ = _.get(filters, "curriculumProgramIds", []);

    const defaultGrades = _.get(
      state,
      "teacher.selected_class.selected_grades",
      []
    );
    const currentUserId = userInfo.id;

    const curriculumProgramIds = isCombinedFiltersEnabled
      ? curriculumProgramIds_
      : curriculumProgramId
      ? [curriculumProgramId]
      : [];

    const allAcademicYears = getAcademicYearsList(organizationId);
    const academicYearIds = _.map(allAcademicYears, "value");

    const isLearningCoursesEnabled = getIsLearningCoursesEnabled();
    const courseSearchText = _.get(state, "schoolLibrary.courseSearchText", "");
    const creatorSearchText = _.get(
      state,
      "schoolLibrary.creatorSearchText",
      ""
    );

    const curriculumPrograms = getUserCurriculumPrograms(state);
    const organizationDetails = getOrganizationDetailsFromCache(organizationId);
    const allCurriculumProgramsIds = _.map(curriculumPrograms, "id");
    const gradesData = getFilteredGrades({
      organizationDetails,
      curriculumProgramIds:
        isCombinedFiltersEnabled && !isSchoolOptionsEnabled
          ? curriculumProgramIds
          : allCurriculumProgramsIds,
    });

    const courseQueryVariables = {
      organizationId,
      filters: {
        academicYears: academicYearIds,
        curriculumProgramIds,
        searchText: courseSearchText,
      },
    };

    return {
      filters,
      curriculumProgramType,
      curriculumProgramIds:
        isCombinedFiltersEnabled && !isSchoolOptionsEnabled
          ? allCurriculumProgramsIds
          : curriculumProgramIds,
      academicYearsList,
      gradeList,
      organizationId,
      defaultAcademicYear,
      defaultGrades,
      isLearningCoursesEnabled,
      courseQueryVariables,
      currentUserId,
      isData: true,
      isLoading: false,
      archivalState: "ALL",
      courseGradeFilters,
      skipStaffListQuery,
      userArchivedFilters,
      academicYearIds,
      hideAssessments,
      curriculumProgramId,
      gradesData,
      schoolsData: curriculumPrograms,
      isSchoolOptionsEnabled,
      isCombinedFiltersEnabled,
      creatorSearchText,
    };
  };
  return compose(
    withRouter,
    connect(mapStateToProps),
    getStaffListEnhancer,
    graphql(getOrganizationSubjectsQuery, {
      name: "getOrganizationSubjects",
      skip: ({
        curriculumProgramType,
        curriculumProgramIds,
        isSchoolOptionsEnabled,
        isCombinedFiltersEnabled,
      }) =>
        !_.includes(
          [
            CURRICULUM_TYPE_PYP,
            CURRICULUM_TYPE_MYP,
            CURRICULUM_TYPE_UBD,
            CURRICULUM_TYPE_DP,
          ],
          curriculumProgramType
        ) ||
        (isCombinedFiltersEnabled &&
          !isSchoolOptionsEnabled &&
          _.size(curriculumProgramIds) === 0),
      options: ({ organizationId, curriculumProgramIds }) => ({
        fetchPolicy: "cache-and-network",
        variables: {
          id: organizationId,
          filters: {
            curriculumProgramIds,
            archivalState: "ALL",
          },
        },
      }),
      props({
        getOrganizationSubjects,
        ownProps: { organizationId, curriculumProgramIds, isData, isLoading },
      }) {
        const data = getOrganizationSubjectsFromCache({
          id: organizationId,
          filters: {
            curriculumProgramIds,
            archivalState: "ALL",
          },
        });
        const subjects = _.get(data, "subjects", []);
        const subjectsList = _.map(subjects, subject => ({
          value: subject.id,
          label: subject.name,
          isArchived: subject.isArchived,
        }));
        return {
          isData: !_.isEmpty(data) && isData,
          isLoading:
            isLoading ||
            _.includes([1, 2], getOrganizationSubjects["networkStatus"]),
          subjectsList,
          subjects: _.map(subjects, item => ({
            value: item.id,
            label: item.name,
            grades: _.map(item.grades, grade => grade.id),
            isArchived: item.isArchived,
          })),
          subjectsData: subjects,
        };
      },
    }),
    graphql(getCoursesFeedQuery, {
      name: "getCoursesFeed",
      skip: ({
        isLearningCoursesEnabled,
        isCombinedFiltersEnabled,
        isSchoolOptionsEnabled,
        curriculumProgramIds,
      }) =>
        !isLearningCoursesEnabled ||
        (isCombinedFiltersEnabled &&
          !isSchoolOptionsEnabled &&
          _.size(curriculumProgramIds) === 0),
      options: ({ courseQueryVariables }) => ({
        fetchPolicy: "cache-and-network",
        variables: courseQueryVariables,
      }),
      props({ getCoursesFeed, ownProps: { isData, isLoading } }) {
        const { node, networkStatus, variables } = getCoursesFeed;
        const data = _.get(node, "learningCourses", {});
        const getCoursesListFeed = _.get(data, "edges", []);
        const courseData = _.map(getCoursesListFeed, course => ({
          __typename: _.get(course, "__typename"),
          ..._.get(course, "node", {}),
        }));

        const pageInfo = _.get(data, "pageInfo", {});
        const hasNextPage = _.get(pageInfo, "hasNextPage", false);
        const endCursor = _.get(pageInfo, "endCursor", null);
        const courseList = getCourseList({ data: getCoursesListFeed });

        return {
          isCourseListLoading: _.includes([1, 2], networkStatus),
          courseList,
          courseData,
          hasNextPageForCourseFilter: hasNextPage,
          isFetchingMoreDataForCourseFilter: networkStatus === 3,
          loadMoreCourseFilterFeed: () => {
            getCoursesFeed.fetchMore({
              variables: {
                ...variables,
                after: endCursor,
              },
            });
          },
        };
      },
    }),
    ...(skipLoader ? [] : [withLoader])
  )(WithLELibraryFilters);
};
export default LELibraryFiltersHOC;
